import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import styles from './CaseHeader.module.scss';
import { setupTransitionInTimeline } from './CaseHeader.transitions';
import type { CaseHeaderProps, CaseHeaderRef } from './CaseHeader.data';
import Image from '../../atoms/image/Image';
import Video from '../../atoms/video/Video';
import CitiesNavigation from '../../molecules/cases-cities-navigation/CitiesNavigation';
import type { ImageBlok, Video as VideoType } from '../../../util/storyblok/types/global.types';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

function CaseHeader({
  blok,
  layoutState = 'heroHeader',
  disableTriggerInOnScroll = false,
  scrubTransitionIn = false,
  skipTransitionIn = false,
  canTransitionIn,
  ref,
  ...props
}: CaseHeaderProps & { ref?: Ref<CaseHeaderRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLElement>(null);
  const clientTitleRef = useRef<HTMLHeadingElement>(null);
  const mediaWrapperRef = useRef<HTMLDivElement>(null);
  const mediaInnerHolderRef = useRef<HTMLDivElement>(null);

  const [canCitiesNavigationTransitionIn, setCanCitiesNavigationTransitionIn] = useState(false);

  let accentColor;
  if (blok?.accent_color_foreground?.color && blok?.accent_color_background?.color) {
    accentColor = {
      foreground: blok.accent_color_foreground.color,
      background: blok.accent_color_background.color,
    };
  }

  useThemeScrollBlock(elementRef, blok.has_header_accent, accentColor);
  useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: {
        start: 'top bottom-=120px',
        end: scrubTransitionIn ? 'top top' : 'bottom top',
        scrub: scrubTransitionIn,
      },
    },
    setupOptions: () => ({
      refs: {
        layoutState,
        clientTitleRef,
        titleRef,
        descriptionRef,
        mediaWrapperRef,
        skipTransitionIn,
        mediaInnerHolderRef,
        setCanCitiesNavigationTransitionIn,
      },
      setupTransitionInTimeline,
    }),
  });

  let image;
  if (blok?.key_image && blok.key_image.length > 0) {
    image = blok.key_image[0] as ImageBlok;
  }

  let video;
  if (blok?.video && blok.video.length > 0) {
    video = blok.video[0] as VideoType;
  }

  return (
    <div
      ref={elementRef}
      className={classNames(styles.caseHeader, props.className, styles[layoutState])}
    >
      <div className={classNames(styles.header, 'grid-gutter')}>
        <Heading
          ref={clientTitleRef}
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          as="h1"
          size="label-large"
          transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
          className={classNames(styles.clientTitle)}
        >
          {blok?.client}
        </Heading>

        <Heading
          ref={titleRef}
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          as="h2"
          size="heading-02"
          transitionInType={`${skipTransitionIn ? 'splitCharsNone' : 'splitCharsSlideInUp'}`}
          className={classNames(styles.title)}
        >
          {blok?.title}
        </Heading>

        <Text
          ref={descriptionRef}
          as="p"
          size="body-large"
          disableTriggerInOnScroll={layoutState !== 'previewNext'}
          transitionInType={`${skipTransitionIn ? 'none' : 'splitLineRotateIn'}`}
          className={classNames(styles.description)}
        >
          {blok?.short_description}
        </Text>
      </div>

      <CitiesNavigation
        className={styles.citiesNavigation}
        blok={blok}
        layoutState={layoutState}
        canTransitionIn={canCitiesNavigationTransitionIn}
        onTransitionInComplete={() => setCanCitiesNavigationTransitionIn(false)}
        skipTransitionIn={skipTransitionIn}
      />

      <div ref={mediaWrapperRef} className={classNames(styles.mediaWrapper)}>
        <div className={classNames(styles.mediaHolder)}>
          <div
            ref={mediaInnerHolderRef}
            className={classNames(styles.mediaInnerHolder, 'abs-fill')}
          >
            {(image?.image_landscape || image?.image_portrait) && !video && (
              <Image
                src={image.image_landscape || image.image_portrait}
                srcDesktop={image.image_landscape || image.image_portrait}
                className={classNames(styles.image, 'abs-fill')}
                innerTransition={{
                  type: 'scaleUp',
                }}
                layout="fill"
                alt={image?.alt_text}
              />
            )}

            {video && (
              <Video
                blok={video}
                objectFit="cover"
                controls
                pauseWhenOutViewport
                playButton
                innerTransition={{
                  type: 'scaleUp',
                }}
                muted={false}
                className={classNames(styles.video, 'abs-fill')}
              />
            )}
          </div>
        </div>
      </div>

      {!!blok?.tag_list?.length && (
        <ul className={classNames(styles.tagList, 'grid-gutter')}>
          {blok.tag_list.map((tag) => (
            <Text
              key={tag}
              disableTriggerInOnScroll={false}
              as="li"
              size="button-label-small"
              className={classNames(styles.tagListItem)}
            >
              {tag}
            </Text>
          ))}
        </ul>
      )}
    </div>
  );
}

CaseHeader.displayName = 'CaseHeader';

export default CaseHeader;
